import { Injectable } from '@angular/core';

import { FeatureFlag, FeatureFlagService } from '@nx-ahs/feature-flags';
import { SearchService } from '@nx-ahs/search';

import { ApiCallServiceParameters } from '../../modules/security/models/client-config/account-screen-config/api-call-details.model';
import { SecurityService } from './security.service';

/**
 * Configuration for refreshing a search-result row from an external system (e.g. MMIS) when it is selected.
 * Same shape as `apiCallDetails.serviceParameters` in the client config, plus the sync-specific options.
 */
export interface VerificationSyncConfig extends ApiCallServiceParameters {
	/** Give up waiting for the service after this many ms. Defaults to DEFAULT_VERIFICATION_SYNC_TIMEOUT_MS. */
	timeoutMs?: number;
	/** Shown to the agent when the refresh fails, instead of the default message. */
	warningMessage?: string;
}

export type VerificationSyncFailureReason = 'disabled' | 'no-data' | 'timeout' | 'error';

export type VerificationSyncResult =
	| { ok: true; row: Record<string, unknown> }
	| { ok: false; reason: VerificationSyncFailureReason };

export interface AccountRefreshResult {
	/** 'skipped' when the flag is off or the account type has no refresh configured. */
	status: 'skipped' | 'started' | 'failed';
	/** Id the account screen waits on; null unless the refresh started a background task. */
	backgroundTaskId: string | null;
}

export const DEFAULT_VERIFICATION_SYNC_TIMEOUT_MS = 10000;

/**
 * Refreshes data from an external system through the generic `ExternalServiceCallFromHT` endpoint.
 * Which service to call, with what inputs, is entirely client-config driven -- nothing here knows which
 * contract or external system is behind the service. Neither method throws; a failed refresh is reported
 * through the result so callers can warn the agent and carry on with the data they already have.
 * Everything is gated by FeatureFlag.ExternalDataSync and does nothing when the flag is off.
 */
@Injectable({
	providedIn: 'root'
})
export class ExternalSyncService {
	constructor(
		private searchService: SearchService,
		private securityService: SecurityService,
		private featureFlagService: FeatureFlagService
	) {}

	public get isEnabled(): boolean {
		return this.featureFlagService.getFeature(FeatureFlag.ExternalDataSync);
	}

	/** True when the flag is on and the config names a service to call. */
	public isVerificationSyncAvailable(config: VerificationSyncConfig | null | undefined): boolean {
		return this.isEnabled && config?.serviceId != null;
	}

	/**
	 * Synchronously refreshes one result row: the service updates the stored data and returns the refreshed row,
	 * which has the same shape as the original row so the caller can overlay it.
	 */
	public async syncVerificationRow(
		config: VerificationSyncConfig,
		row: Record<string, unknown>
	): Promise<VerificationSyncResult> {
		if (!this.isVerificationSyncAvailable(config)) {
			return { ok: false, reason: 'disabled' };
		}

		const { inputs, environmentKeys } = this.buildCallInputs(config, row);
		const timeoutMs = config.timeoutMs ?? DEFAULT_VERIFICATION_SYNC_TIMEOUT_MS;

		let timer: ReturnType<typeof setTimeout> | undefined;
		const timedOut = new Promise<'timeout'>((resolve) => {
			timer = setTimeout(() => resolve('timeout'), timeoutMs);
		});

		try {
			const outcome = await Promise.race([
				this.searchService.makeExternalServiceCall(
					config.serviceId,
					inputs,
					environmentKeys,
					config.useVault ?? false,
					false
				),
				timedOut
			]);

			if (outcome === 'timeout') {
				return { ok: false, reason: 'timeout' };
			}

			const refreshed = this.parseFirstRow(outcome);
			return refreshed ? { ok: true, row: refreshed } : { ok: false, reason: outcome ? 'no-data' : 'error' };
		} catch (error) {
			console.error('Verification sync failed', error);
			return { ok: false, reason: 'error' };
		} finally {
			clearTimeout(timer);
		}
	}

	/**
	 * Starts the account-data refresh configured for the account type. The call returns as soon as the service has
	 * the data to show; the database update keeps running as a background task whose id is left in
	 * SearchService, which is where the account screen picks it up (EncounterService.getBackgroundTaskId).
	 */
	public async startAccountRefresh(
		accountTypeId: number,
		row: Record<string, unknown>
	): Promise<AccountRefreshResult> {
		if (!this.isEnabled) {
			return { status: 'skipped', backgroundTaskId: null };
		}

		const params = this.securityService
			.getCurrentApp()
			?.application?.clientConfig?.accountScreenConfigs?.find((x) => x.accountTypeId == accountTypeId)
			?.apiCallDetails?.serviceParameters;

		if (!params?.apiCallEnabled || params.serviceId == null) {
			return { status: 'skipped', backgroundTaskId: null };
		}

		const { inputs, environmentKeys } = this.buildCallInputs(params, row);

		try {
			const result = await this.searchService.makeExternalServiceCall(
				params.serviceId,
				inputs,
				environmentKeys,
				params.useVault ?? false,
				true
			);

			if (!result) {
				return { status: 'failed', backgroundTaskId: null };
			}

			return { status: 'started', backgroundTaskId: this.searchService.backgroundTaskId };
		} catch (error) {
			console.error('Account refresh failed', error);
			return { status: 'failed', backgroundTaskId: null };
		}
	}

	/** rowData maps service input names to row keys; environmentSettings is passed through for the API to resolve. */
	private buildCallInputs(
		params: ApiCallServiceParameters,
		row: Record<string, unknown>
	): { inputs: Record<string, unknown>; environmentKeys: Record<string, string> } {
		const inputs: Record<string, unknown> = {};
		for (const [inputName, rowKey] of Object.entries(params.rowData ?? {})) {
			inputs[inputName] = row?.[rowKey];
		}
		return { inputs, environmentKeys: params.environmentSettings ?? {} };
	}

	/** The service wraps its rows as `{ data: [...] }` inside the JSON `content` string of the response. */
	private parseFirstRow(result: unknown): Record<string, unknown> | null {
		const content = typeof result === 'object' && result ? (result as { content?: string }).content : undefined;
		if (!content) {
			return null;
		}

		try {
			const data = JSON.parse(content)?.data;
			const first = Array.isArray(data) ? data[0] : null;
			return first && typeof first === 'object' ? (first as Record<string, unknown>) : null;
		} catch {
			return null;
		}
	}
}
